<template>
  <div class="user-management-container">
    <div class="page-header">
      <h2 class="page-title">
        <i class="el-icon-user-solid mr-2"></i>
        {{ isEdit ? '编辑用户' : '添加用户' }}
      </h2>
      <div class="page-actions">
        <el-button icon="el-icon-back" @click="$router.push('/user/list')">返回列表</el-button>
      </div>
    </div>

    <el-card class="form-card" shadow="hover">
      <div slot="header" class="card-header">
        <span>{{ isEdit ? '用户信息编辑' : '新建用户' }}</span>
        <small v-if="isEdit" class="text-muted">ID: {{ userForm.id }}</small>
      </div>
      
      <el-form 
        ref="userForm" 
        :model="userForm" 
        :rules="userRules" 
        label-width="100px"
        status-icon>
        
        <el-divider content-position="left">基本信息</el-divider>
        
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="用户名" prop="username">
              <el-input 
                v-model="userForm.username" 
                prefix-icon="el-icon-user"
                placeholder="请输入用户名">
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="名称" prop="realName">
              <el-input 
                v-model="userForm.realName" 
                prefix-icon="el-icon-edit"
                placeholder="请输入名称">
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="密码" :prop="isEdit ? '' : 'password'">
              <el-input 
                v-model="userForm.password" 
                type="password"
                prefix-icon="el-icon-lock" 
                placeholder="请输入密码">
              </el-input>
              <div class="form-tip" v-if="isEdit">不修改密码请留空</div>
              <div class="form-tip pwd-strength-tip" v-if="!isEdit || userForm.password">密码须≥8位，包含大小写字母、数字及特殊符号</div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="确认密码" :prop="isEdit ? '' : 'confirmPassword'">
              <el-input 
                v-model="userForm.confirmPassword" 
                type="password" 
                prefix-icon="el-icon-lock"
                placeholder="请再次输入密码">
              </el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="手机号" prop="phoneNumber">
              <el-input 
                v-model="userForm.phoneNumber" 
                prefix-icon="el-icon-mobile-phone"
                placeholder="请输入手机号">
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="状态">
              <el-radio-group v-model="userForm.status">
                <el-radio :label="1">
                  <i class="el-icon-success text-success"></i> 正常
                </el-radio>
                <el-radio :label="0">
                  <i class="el-icon-error text-danger"></i> 禁用
                </el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-divider content-position="left">角色分配</el-divider>
        
        <el-form-item label="角色" prop="roleIds">
          <div class="role-selection">
            <el-checkbox-group v-model="userForm.roleIds">
              <el-checkbox 
                v-for="role in roleOptions" 
                :key="role.id" 
                :label="Number(role.id)"
                class="role-checkbox">
                {{ role.roleName }}
              </el-checkbox>
            </el-checkbox-group>
          </div>
        </el-form-item>

        <el-divider content-position="left">桥梁管理</el-divider>
        
        <el-form-item label="关联桥梁">
          <bridge-transfer
            ref="bridgeTransfer"
            v-model="userForm.bridgeIds"
            :bridge-list="bridgeOptions"
            :loading="loading"
            :user-id="userForm.id"
            :auto-load="isEdit"
            @change="handleBridgeChange"
            @loaded="handleBridgesLoaded"
            @load-error="handleBridgeLoadError"
          ></bridge-transfer>
        </el-form-item>

        <el-divider></el-divider>
        
        <el-form-item class="form-actions">
          <el-button 
            type="primary" 
            icon="el-icon-check" 
            @click="submitForm" 
            :loading="loading">
            保存
          </el-button>
          <el-button icon="el-icon-refresh-left" @click="resetForm">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { encryptPassword } from '@/utils/password'
import { getAllRoles, getUserComplete, saveUserComplete } from '@/api/auth'
import { getAllBridges } from '@/api/bridge'
import BridgeTransfer from '@/components/common/BridgeTransfer.vue'

export default {
  name: 'UserManagement',
  components: {
    BridgeTransfer
  },
  data() {
    // 验证密码强度
    const validatePasswordStrength = (rule, value, callback) => {
      if (!value) {
        // 编辑模式下密码为空表示不修改，跳过强度校验
        if (this.isEdit) {
          return callback()
        }
        return callback(new Error('请输入密码'))
      }
      if (value.length < 8) {
        return callback(new Error('密码长度不能少于8位'))
      }
      if (!/[A-Z]/.test(value)) {
        return callback(new Error('密码必须包含大写字母'))
      }
      if (!/[a-z]/.test(value)) {
        return callback(new Error('密码必须包含小写字母'))
      }
      if (!/[0-9]/.test(value)) {
        return callback(new Error('密码必须包含数字'))
      }
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(value)) {
        return callback(new Error('密码必须包含特殊符号（如 !@#$%^&* 等）'))
      }
      callback()
    }

    // 验证密码一致性
    const validatePassword = (rule, value, callback) => {
      if (value !== this.userForm.password) {
        callback(new Error('两次输入密码不一致'))
      } else {
        callback()
      }
    }
    
    return {
      loading: false,
      userForm: {
        id: null,
        username: '',
        realName: '',
        password: '',
        confirmPassword: '',
        phoneNumber: '',
        roleIds: [],
        bridgeIds: [],
        status: 1
      },
      roleOptions: [],
      bridgeOptions: [],
      userRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '长度在 3 到 20 个字符', trigger: 'blur' }
        ],
        realName: [
          { required: true, message: '请输入真实姓名', trigger: 'blur' }
        ],
        password: [
          { validator: validatePasswordStrength, trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入密码', trigger: 'blur' },
          { validator: validatePassword, trigger: 'blur' }
        ],
        phoneNumber: [
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ],
        roleIds: [
          { type: 'array', required: true, message: '请至少选择一个角色', trigger: 'change' }
        ]
      },
      isEdit: false
    }
  },
  computed: {
    // 检查是否有缺失的桥梁（现在由BridgeTransfer组件处理）
    hasMissingBridges() {
      return false;
    }
  },
  created() {
    // 获取所有必要数据
    Promise.all([this.fetchRoleOptions(), this.fetchBridgeOptions()])
      .then(() => {
        console.log("角色列表与桥梁列表加载完成");
        
        // 如果是编辑模式，获取用户信息
        const userId = this.$route.params.id;
        if (userId) {
          this.isEdit = true;
          this.fetchUserCompleteData(userId);
        }
      })
      .catch(error => {
        console.error("数据初始化出错:", error);
      });
  },
  
  mounted() {
    // 组件挂载后，确保数据已经正确绑定
  },
  
  updated() {
    // 组件更新后，检查桥梁ID数据
  },
  methods: {
    // 获取所有角色
    async fetchRoleOptions() {
      try {
        const res = await getAllRoles()
        if (res.code === 200) {
          this.roleOptions = res.data
        } else {
          this.$message.error(res.message || '获取角色列表失败')
        }
      } catch (error) {
        console.error('获取角色列表出错:', error)
        this.$message.error('获取角色列表失败')
      }
    },
    
    // 获取所有桥梁
    async fetchBridgeOptions() {
      try {
        this.loading = true;
        const res = await getAllBridges()
        if (res.code === 200) {
          // 确保桥梁数据格式正确，适配BridgeTransfer组件
          this.bridgeOptions = res.data.map(bridge => ({
            id: Number(bridge.id),
            bridgeName: bridge.name || bridge.bridgeName,
            key: Number(bridge.id)
          }));
          console.log('已加载桥梁数据:', this.bridgeOptions.length, '条');
        } else {
          this.$message.error(res.message || '获取桥梁列表失败')
        }
      } catch (error) {
        console.error('获取桥梁列表出错:', error)
        this.$message.error('获取桥梁列表失败')
      } finally {
        this.loading = false;
      }
    },
    
    // 检查并修复权限与桥梁ID不匹配的问题
    checkAndFixPermissionBridgeIdMismatch(userData) {
      if (!userData.permissions || !userData.bridgeIds) return;
      
      // 从权限中提取桥梁ID
      const permissionBridgeIds = userData.permissions
        .filter(p => p.includes('BRIDGE_ADMIN_') || p.includes('DUTY_ROOM_USER_'))
        .map(p => {
          const parts = p.split('_');
          return Number(parts[parts.length - 1]);
        });
      
      // 转换bridgeIds为数字类型
      const currentBridgeIds = userData.bridgeIds.map(id => Number(id));
      
      // 检查是否有不匹配
      const missingInPermissions = currentBridgeIds.filter(id => !permissionBridgeIds.includes(id));
      const missingInBridgeIds = permissionBridgeIds.filter(id => !currentBridgeIds.includes(id));
      
      if (missingInPermissions.length > 0 || missingInBridgeIds.length > 0) {
        // 使用权限中的桥梁ID作为真实的桥梁ID
        return permissionBridgeIds;
      }
      
      return null; // 没有不匹配的情况
    },
    
    // 获取用户完整数据
    async fetchUserCompleteData(userId) {
      try {
        this.loading = true;
        const res = await getUserComplete(userId)
        if (res.code === 200) {
          
          // 设置表单数据
          const userData = res.data
          
          // 检查并修复权限与桥梁ID不匹配的问题
          const fixedBridgeIds = this.checkAndFixPermissionBridgeIdMismatch(userData);
          
          // 从权限中提取真实的桥梁ID
          let realBridgeIds = [];
          if (fixedBridgeIds) {
            realBridgeIds = fixedBridgeIds;
          } else if (userData.permissions && userData.permissions.length > 0) {
            // 解析权限字符串，提取实际的桥梁ID
            // 格式如：BRIDGE_ADMIN_15 或 DUTY_ROOM_USER_7
            const bridgePermissions = userData.permissions.filter(
              p => p.includes('BRIDGE_ADMIN_') || p.includes('DUTY_ROOM_USER_')
            );
            
            if (bridgePermissions.length > 0) {
              realBridgeIds = bridgePermissions.map(permission => {
                const parts = permission.split('_');
                const idStr = parts[parts.length - 1];
                return Number(idStr);
              });
            }
          }
          
          // 确保存储的是数字类型，优先使用从权限中提取的ID
          const bridgeIds = realBridgeIds.length > 0 
            ? realBridgeIds 
            : (userData.bridgeIds || []).map(id => Number(id)).filter(id => id > 0);
          
          console.log('用户关联桥梁ID:', bridgeIds);
          
          this.userForm = {
            id: userData.id,
            username: userData.username,
            realName: userData.realName || '',
            password: '', // 不显示密码
            confirmPassword: '',
            phoneNumber: userData.phoneNumber || '',
            roleIds: Array.isArray(userData.roleIds) ? userData.roleIds.map(id => Number(id)) : [],
            bridgeIds: bridgeIds,
            status: userData.status
          }
          
          return userData;
        } else {
          this.$message.error(res.message || '获取用户信息失败')
          return null;
        }
      } catch (error) {
        console.error('获取用户信息出错:', error)
        this.$message.error('获取用户信息失败')
        return null;
      } finally {
        this.loading = false;
      }
    },
    
    // 处理桥梁选择变化
    handleBridgeChange(selectedIds) {
      // 确保所有值都是数字类型
      const bridgeIds = selectedIds.map(id => Number(id)).filter(id => id > 0);
      this.userForm.bridgeIds = bridgeIds;
      console.log('桥梁选择变化:', bridgeIds);
    },
    
    // 处理桥梁加载完成
    handleBridgesLoaded(bridgeIds) {
      console.log('用户桥梁加载完成:', bridgeIds);
      // 如果需要，可以在这里做一些额外处理
    },
    
    // 处理桥梁加载错误
    handleBridgeLoadError(errorMsg) {
      this.$message.error(errorMsg || '获取用户桥梁关联失败');
    },
    
    // 提交表单
    submitForm() {
      this.$refs.userForm.validate(async valid => {
        if (valid) {
          this.loading = true
          try {
            // 准备提交的数据
            const userData = {
              username: this.userForm.username,
              realName: this.userForm.realName,
              phoneNumber: this.userForm.phoneNumber,
              status: this.userForm.status,
              roleIds: this.userForm.roleIds.map(id => Number(id)),
              bridgeIds: this.userForm.bridgeIds.map(id => Number(id))
            }
            
            // 如果是编辑模式，添加用户ID
            if (this.isEdit) {
              userData.id = this.userForm.id
            }
            
            // 如果新增用户或者修改用户密码，则加密密码
            if (!this.isEdit || this.userForm.password) {
              // 加密密码
              userData.password = encryptPassword(this.userForm.password)
              userData.confirmPassword = userData.password
            }

            // 使用统一的complete接口
            const res = await saveUserComplete(userData)
            
            if (res.code === 200) {
              this.$message.success(this.isEdit ? '用户更新成功' : '用户创建成功')
              this.$router.push('/user/list')
            } else {
              this.$message.error(res.message || (this.isEdit ? '用户更新失败' : '用户创建失败'))
            }
          } catch (error) {
            console.error(this.isEdit ? '更新用户出错:' : '创建用户出错:', error)
            this.$message.error(this.isEdit ? '用户更新失败' : '用户创建失败')
          } finally {
            this.loading = false
          }
        } else {
          return false
        }
      })
    },
    
    // 重置表单
    resetForm() {
      this.$refs.userForm.resetFields()
      this.userForm.bridgeIds = []
    }
  }
}
</script>

<style scoped>
/* 基础布局 */
.user-management-container {
  padding: 1.25rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
}

.page-title {
  font-size: 1.25rem;
  color: #333;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
}

.mr-2 {
  margin-right: 0.5rem;
}

.form-card {
  margin-bottom: 1.25rem;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f5f7fa;
  padding: 0.5rem;
}

.text-muted {
  color: #909399;
  font-size: 0.875rem;
  margin-left: 0.5rem;
}

.bridge-role-type {
  margin-bottom: 10px;
}

.form-tip {
  font-size: 0.75rem;
  color: #909399;
  margin-top: 0.25rem;
}

.pwd-strength-tip {
  color: #e6a23c;
}

.text-success {
  color: #67c23a;
}

.text-danger {
  color: #f56c6c;
}

/* 角色和权限样式 */
.role-selection {
  display: flex;
  flex-wrap: wrap;
  padding: 0.5rem;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.role-checkbox {
  background-color: #fff;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin: 0.25rem;
}

.form-actions {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

/* 自定义ElementUI组件样式 */
:deep(.el-form-item__label) {
  font-weight: 500;
}

:deep(.el-checkbox) {
  margin-right: 0.25rem;
  margin-bottom: 0.25rem;
}

:deep(.el-radio) {
  margin-right: 1.5rem;
}

:deep(.el-divider__text) {
  color: #606266;
  font-weight: 500;
}

/* 卡片和按钮样式 */
:deep(.el-card__header) {
  padding: 0.5rem 1rem;
  background-color: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
}

:deep(.el-button--primary) {
  background-color: #409eff;
  border-color: #409eff;
}

:deep(.el-button--primary:hover) {
  background-color: #66b1ff;
  border-color: #66b1ff;
}

:deep(.el-input__inner:focus) {
  border-color: #409eff;
}

:deep(.el-radio__input.is-checked .el-radio__inner) {
  border-color: #409eff;
  background-color: #409eff;
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  border-color: #409eff;
  background-color: #409eff;
}
</style> 